import { DIST_EPSILON_SQUARED } from "./core";
import { polyLine2Edge, polyLine2EdgeEval } from "./polyLine2";
import { PolyLine2, polyLine2New } from "./polyLine2Types";
import { vec2Perp, vec2Dist, vec2DistSquared, vec2Normalize, vec2Scale } from "./vec2";

// Functions working with 2 vertex `PolyLine2`s.

export function line2(a1: PolyLine2, a2: PolyLine2): PolyLine2 {
  const line = polyLine2New(4);
  line[0] = a1[0];
  line[1] = a1[1];
  line[2] = a2[0];
  line[3] = a2[1];
  return line;
}

export function line2VecTangent(line: PolyLine2, neg = false): PolyLine2 {
  const x = line[2] - line[0];
  const y = line[3] - line[1];
  if (neg === false) {
    const returnVec = vec2Normalize(x, y);
    return PolyLine2.from(returnVec);
  } else {
    const returnVec = vec2Normalize(-x, -y);
    return PolyLine2.from(returnVec);
  }
}

export function line2VecPerp(line: PolyLine2, neg = false): PolyLine2 {
  const x = line[2] - line[0];
  const y = line[3] - line[1];
  if (neg === false) {
    const perpVec = vec2Perp(-x, -y);
    const returnVec = vec2Normalize(perpVec[0], perpVec[1]);
    return PolyLine2.from(returnVec);
  } else {
    const perpVec = vec2Perp(x, y);
    const returnVec = vec2Normalize(perpVec[0], perpVec[1]);
    return PolyLine2.from(returnVec);
  }
}

export function line2SVL(lineStart: PolyLine2, vector: PolyLine2, length: number) {
  const newVector = polyLine2New(4);

  if (vector.length === 2) {
    newVector[2] = vector[0];
    newVector[3] = vector[1];
    newVector[0] = 0;
    newVector[1] = 0;

    vector = newVector;
  }
  const xVal = vector[2] - vector[0];
  const yVal = vector[3] - vector[1];

  const vecNorm = vec2Normalize(xVal, yVal);

  const moveVec = vec2Scale(vecNorm[0], vecNorm[1], length);

  const theLine = polyLine2New(4);

  theLine[0] = lineStart[0];
  theLine[1] = lineStart[1];
  theLine[2] = lineStart[0] + moveVec[0];
  theLine[3] = lineStart[1] + moveVec[1];

  return theLine;
}

export function lineLength(line: PolyLine2): number {
  const length = vec2Dist(line[0], line[1], line[2], line[3]);
  return length;
}

export function lineEval(line: PolyLine2, dist: number, dirFlag: boolean = false): PolyLine2 {
  const length = vec2Dist(line[0], line[1], line[2], line[3]);
  let newDist = 0;
  if (dirFlag === false) {
    newDist = dist / length;
  } else {
    newDist = 1 - dist / length;
  }
  const [x1, y1] = polyLine2EdgeEval(line, 0, newDist, true);
  const thePt = polyLine2New(2);
  thePt[0] = x1;
  thePt[1] = y1;

  return thePt;
}

export function pt2(x: number, y: number): PolyLine2 {
  // turns an x,y value into a PolyLine2(2)
  const thePt = polyLine2New(2);
  thePt[0] = x;
  thePt[1] = y;
  return thePt;
}

export function lineEndPt(line: PolyLine2): PolyLine2 {
  const endPt = polyLine2EdgeEval(line, 0, 1, true);
  const ptList = polyLine2New(2);
  ptList[0] = endPt[0];
  ptList[1] = endPt[1];
  pt2(ptList[0], ptList[1]);
  return ptList;
}

export function lineStartPt(line: PolyLine2): PolyLine2 {
  const startPt = polyLine2EdgeEval(line, 0, 0, true);
  const ptList = polyLine2New(2);
  ptList[0] = startPt[0];
  ptList[1] = startPt[1];
  pt2(ptList[0], ptList[1]);
  return ptList;
}

export function lineMidPt(line: PolyLine2): PolyLine2 {
  const midPt = polyLine2EdgeEval(line, 0, 0.5, true);
  const ptList = polyLine2New(2);
  ptList[0] = midPt[0];
  ptList[1] = midPt[1];
  pt2(ptList[0], ptList[1]);
  return ptList;
}

export function getLongestLineIndex(line: PolyLine2) {
  const length = line.length;
  const lineSegments = length / 2;

  const lines: Float64Array[] = [];
  let longestLineIndex = 0;
  for (let i = 0; i < lineSegments; i += 1) {
    lines.push(polyLine2Edge(line, i));
    if (lineLength(polyLine2Edge(line, i)) > lineLength(polyLine2Edge(line, longestLineIndex))) {
      longestLineIndex = i;
    }
  }
  return longestLineIndex;
}

export function getShortestLineIndex(line: PolyLine2) {
  const length = line.length;
  const lineSegments = length / 2;

  const lines: Float64Array[] = [];
  let shortestLineIndex = 0;
  for (let i = 0; i < lineSegments; i += 1) {
    lines.push(polyLine2Edge(line, i));
    if (lineLength(polyLine2Edge(line, i)) < lineLength(polyLine2Edge(line, shortestLineIndex))) {
      shortestLineIndex = i;
    }
  }
  return shortestLineIndex;
}

export function line2Equal(line1: PolyLine2, line2: PolyLine2): boolean {
  return (
    vec2DistSquared(line1[0], line1[1], line2[0], line2[1]) < DIST_EPSILON_SQUARED &&
    vec2DistSquared(line1[2], line1[3], line2[2], line2[3]) < DIST_EPSILON_SQUARED
  );
}
